import styled from "styled-components"

import { colors } from "@minuthq/meatball-ui-react"

import { MButton, TMButtonProps } from "src/ui/Button/MButton"
import { MText, MTextProps } from "src/ui/MText"
import { spacing } from "src/ui/spacing"

type TSize = "small" | "medium" | "large"
type BannerButton = {
  label: string
  onClick: () => void
  buttonProps?: TMButtonProps
}
export type TAlert =
  | "good"
  | "info"
  | "warning"
  | "neutral"
  | "error"
  | "default"

export type MBannerProps = {
  type?: TAlert
  size?: TSize
  fullWidth?: boolean
  show?: boolean
  hideBorder?: boolean
  style?: React.CSSProperties
  children?: React.ReactNode
  title?: React.ReactNode
  icon?: React.ReactNode
  buttons?: BannerButton[]
  horizontal?: boolean
}

export function MBanner({
  type = "default",
  size = "medium",
  fullWidth = false,
  show = true,
  hideBorder,
  children,
  icon,
  title,
  buttons = [],
  horizontal,
  ...props
}: MBannerProps) {
  if (!show) {
    return null
  }

  const AlertStyle = getAlertStyle(type)

  return (
    <AlertStyle
      size={size}
      $fullWidth={fullWidth}
      $hideBorder={hideBorder}
      {...props}
    >
      <ContentBox $horizontal={horizontal}>
        {icon && <Icon $horizontal={horizontal}>{icon}</Icon>}
        {title && (
          <MText variant="subtitle" color="unset">
            {title}
          </MText>
        )}
        {children && (
          <Body $horizontal={horizontal}>
            <MText variant={getTextVariant(size)} color="unset">
              {children}
            </MText>
          </Body>
        )}
        {!!buttons.length && (
          <Buttons $horizontal={horizontal}>
            {buttons.map(({ label, onClick, buttonProps }) => (
              <MButton
                key={label}
                variant="subtle"
                size="small"
                onClick={onClick}
                {...buttonProps}
              >
                {label}
              </MButton>
            ))}
          </Buttons>
        )}
      </ContentBox>
    </AlertStyle>
  )
}

const Icon = styled.div<{ $horizontal?: boolean }>`
  ${({ $horizontal }) => $horizontal && "grid-row: span 2;"}
`

const Body = styled.div<{ $horizontal?: boolean }>`
  grid-column: ${({ $horizontal }) => (!$horizontal ? "span 2" : "2")};
`

const Buttons = styled.div<{ $horizontal?: boolean }>`
  display: flex;
  gap: ${spacing.S};
  flex-wrap: wrap;
  grid-column: ${({ $horizontal }) => (!$horizontal ? "span 2" : "2")};
  margin-top: ${({ $horizontal }) => ($horizontal ? spacing.S : "0")};
`

const ContentBox = styled.div<{ $horizontal?: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: ${({ $horizontal }) => ($horizontal ? "center" : "start")};
  column-gap: ${spacing.S};
  row-gap: ${({ $horizontal }) => (!$horizontal ? spacing.M : spacing.XS3)};
`

function getAlertStyle(type: TAlert) {
  switch (type) {
    case "good":
      return Good
    case "info":
      return Info
    case "warning":
      return Warning
    case "error":
      return Error
    case "neutral":
      return Neutral
    default:
      return Default
  }
}

function getTextVariant(size: TSize): MTextProps["variant"] {
  switch (size) {
    case "small":
      return "bodyS"
    default:
      return "body"
  }
}

type TAlertStyle = {
  size?: TSize
  $fullWidth?: boolean
  $hideBorder?: boolean
}

const Base = styled.div<TAlertStyle>`
  background: ${colors.neutral};
  ${({ size }) => {
    switch (size) {
      case "small":
        return `padding: 1px ${spacing.XS};`
      case "medium":
        return `padding: ${spacing.M};`
      default:
        return `padding: ${spacing.L};`
    }
  }}
  border-radius: ${(props) => (!props.$hideBorder ? "0.875rem" : "0")};
  display: ${(props) => (props.$fullWidth ? "block" : "inline-block")};
  flex-basis: ${(props) => (props.$fullWidth ? "100%" : "auto")};
`

const Default = styled(Base)`
  background: inherit;
  border-color: ${colors.neutral};
  border-width: ${(props) => (!props.$hideBorder ? "1px" : "0px")};
  border-style: solid;
  color: inherit;
`

const Neutral = styled(Base)`
  color: ${colors.textPrimary};
`

const Good = styled(Base)`
  background: ${colors.systemGoodLight};
  color: ${colors.systemGoodDark};
`

const Info = styled(Base)`
  background: ${colors.systemInfoLight};
  color: ${colors.systemInfoDark};
`

const Warning = styled(Base)`
  background: ${colors.systemWarningLight};
  color: ${colors.systemWarningDark};
`

const Error = styled(Base)`
  background: ${colors.systemErrorLight};
  color: ${colors.systemErrorDark};
`
